import JSZip from "jszip";
import i18next from "i18next";
import { globalConfig } from "./globalConfig";


export interface ICapturedFrame {
    filename: string;
    dataUrl: string;
}


/**
 * Clone a File object
 * @param file - The file to be cloned
 * @returns A new File object with the same content, name and type
 */
export async function cloneFile(file: File): Promise<File> {
    if (!(file instanceof File)) {
        throw new Error('Parameter must be a File object');
    }

    const arrayBuffer = await file.arrayBuffer();
    return new File([arrayBuffer], file.name, {
        type: file.type,
        lastModified: file.lastModified
    });
}

/**
 * Clone a File object synchronously (using Blob slice)
 * @param file - The file to be cloned
 * @returns A new File object with the same content, name and type
 */
// export function cloneFileSync(file: File): File {
//     if (!(file instanceof File)) {
//         throw new Error('Parameter must be a File object');
//     }

//     return new File([file], file.name, {
//         type: file.type,
//         lastModified: file.lastModified
//     });
// }

// /**
//  * Generate a JSON file from an object and return as a binary stream (Blob)
//  * @param sourceObject - The object to be converted to JSON
//  * @returns A Blob containing the JSON data
//  */
// export function generateJsonBlob(sourceObject: Object): Blob {
//     if (!sourceObject || typeof sourceObject !== 'object') {
//         throw new Error('Parameter must be a valid object');
//     }

//     try {
//         // Convert object to JSON string with formatting
//         const jsonString = JSON.stringify(sourceObject, null, 2);

//         // Create a Blob with JSON content type
//         const blob = new Blob([jsonString], {
//             type: 'application/json'
//         });

//         return blob;
//     } catch (error) {
//         throw new Error(`Failed to generate JSON blob: ${error instanceof Error ? error.message : String(error)}`);
//     }
// }

// /**
//  * Generate a JSON file from an object and return as ArrayBuffer
//  * @param sourceObject - The object to be converted to JSON
//  * @returns Promise<ArrayBuffer> containing the JSON data
//  */
// export async function generateJsonArrayBuffer(sourceObject: Object): Promise<ArrayBuffer> {
//     const blob = generateJsonBlob(sourceObject);
//     return await blob.arrayBuffer();
// }

/**
 * Set the selection dropdown 设置下拉框选项及事件
 * @param domid - The ID of the dropdown
 */
export function setSelectionDropdown(domid: string) {
    const dropdownItems = document.querySelectorAll(`#${domid}ListDom .vision-dropdown-item`);
    const dropdownToggle = document.getElementById(`${domid}Button`);

    dropdownItems.forEach(item => {
        item.addEventListener('click', function (event) {
            event.preventDefault();
            const selectedValue = item.getAttribute('data-value');
            const selectedText = item.textContent;
            // 更新按钮文本
            if (dropdownToggle) {
                dropdownToggle.textContent = selectedText || selectedValue;
            }

            //   // 更新active状态
            dropdownItems.forEach(item => {
                const itemValue = item.getAttribute('data-value');
                if (itemValue === selectedValue) {
                    item.classList.add('active');
                } else {
                    item.classList.remove('active');
                }
            });
        });
    });
}

/**
 * Get the selection dropdown 获取下拉框选中项
 * @param domid - The ID of the dropdown
 * @returns The value of the selected item
 */
export function getSelectionDropdown(domid: string) {
    const activeItem = document.querySelector(`#${domid}ListDom .vision-dropdown-item.active`) as HTMLElement | null;
    if (activeItem) {
        return activeItem.getAttribute('data-value');
    }
    return null;
}

// 初始化复选框交互
export function setSwitchCheckbox(domid: string) {
    const enableLineDrawingInput = document.getElementById(`${domid}`) as HTMLInputElement | null;
    const enableLineDrawingSwitch = enableLineDrawingInput?.closest('.vision-switch-wrapper')?.querySelector('.vision-switch') as HTMLElement | null;

    if (enableLineDrawingInput && enableLineDrawingSwitch) {
        // 同步初始状态
        const updateSwitchState = () => {
            if (enableLineDrawingInput.checked) {
                enableLineDrawingSwitch.classList.add('checked');
            } else {
                enableLineDrawingSwitch.classList.remove('checked');
            }
        };

        // 监听 input 的 change 事件
        enableLineDrawingInput.addEventListener('change', () => {
            updateSwitchState();
        });

        // 点击 switch 时切换状态
        enableLineDrawingSwitch.addEventListener('click', (e) => {
            e.preventDefault();
            enableLineDrawingInput.checked = !enableLineDrawingInput.checked;
            updateSwitchState();
        });

        // 初始化状态
        updateSwitchState();
    }
}
/**
 * Fetch a JSON file from a remote URL and return its parsed content
 * @param url - The URL of the JSON resource
 * @returns A Promise resolving to the parsed JSON object
 */
export async function fetchJsonFromUrl<T = unknown>(url: string): Promise<T> {
    if (!url || typeof url !== 'string') {
        throw new Error('URL must be a non-empty string');
    }

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch JSON from ${url}: ${response.status} ${response.statusText}`);
        }

        return (await response.json()) as T;
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`Error fetching JSON from ${url}: ${message}`);
    }
}

/**
 * Parse query parameters from a URL and return them as a key-value map
 * @param url - Optional URL string; defaults to current browser location
 * @returns An object containing the decoded query parameters
 */
export function getUrlQueryParams(url?: string): Record<string, string> {
    let searchParams: URLSearchParams;

    if (url) {
        try {
            const parsedUrl = new URL(url);
            searchParams = parsedUrl.searchParams;
        } catch (error) {
            throw new Error(`Invalid URL provided: ${error instanceof Error ? error.message : String(error)}`);
        }
    } else {
        if (typeof window === 'undefined' || !window.location) {
            throw new Error('No URL provided and window.location is unavailable');
        }
        searchParams = new URLSearchParams(window.location.search);
    }

    const params: Record<string, string> = {};

    searchParams.forEach((value, key) => {
        params[key] = value;
    });

    return params;
}

export async function drawDataURLToCanvas(
    dataUrl: string,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
): Promise<void> {
    const image = new Image();
    image.decoding = 'async';
    image.src = dataUrl;
    await image.decode();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
}

export function dataURLToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    if (!header || !base64) {
        throw new Error('Invalid data URL');
    }
    const mimeMatch = header.match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
}

export async function downloadCapturedFramesAsZip(frames: ICapturedFrame[], zipFileName?: string): Promise<void> {
    try {
        const zip = new JSZip();
        for (const frame of frames) {
            const blob = dataURLToBlob(frame.dataUrl);
            zip.file(frame.filename, blob);
        }
        const content = await zip.generateAsync({ type: 'blob' });
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const finalName = `${zipFileName || 'line-drawing'}-${timestamp}.zip`;
        const url = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log(`[VideoExport] 已导出线稿帧 ZIP: ${finalName}`);
    } catch (error) {
        console.warn('[VideoExport] 导出线稿 ZIP 失败:', error);
    }
}

/**
 * Get translated text by i18n key
 * @param key - The i18n translation key
 * @param options - Optional interpolation options for variables
 * @returns The translated text string
 */
export function getI18nText(key: string, options?: Record<string, any>): string {
    try {
        return i18next.t(key, options);
    } catch (error) {
        console.warn(`[I18n] Failed to get translation for key "${key}":`, error);
        return key;
    }
}

/**
 * 
 * @param scene 打开示例场景页面
 */
export function openExampleViewPage(scene: string) {
    const targetUrl = `${globalConfig.ROOT_PATH_URL}/index_demoview.html`;
    const separator = targetUrl.includes('?') ? '&' : '?';
    let fullUrl = `${targetUrl}${separator}scene=${encodeURIComponent(scene)}`;
    
    if (window.self !== window.top) {
        try{
            window.top.location.href = fullUrl;
        }catch(error){
            window.location.href = fullUrl;
        }
    } else {
        window.location.href = fullUrl;
    }

}

export function openHomepage(){
    if(window.self !== window.top){
        try{
            window.top.location.href = globalConfig.HOMEPAGE_PATH_URL;
        }catch(error){
            window.location.href = globalConfig.HOMEPAGE_PATH_URL;
        }
    } else {
        window.location.href = globalConfig.HOMEPAGE_PATH_URL;
    }   
}

export async function selectFolder() {
    try {
        // 通过postMessage请求主窗口帮助
        window.parent.postMessage({
            type: 'SHOW_DIRECTORY_PICKER_REQUEST',
            id: Date.now() // 唯一ID用于回调
        }, '*');
    } catch (error) {
        console.error('Failed to request folder picker:', error);
    }
}

/**
 * Convert a color string to standard hexadecimal format (uppercase)
 * 将字符串转为标准颜色十六进制表示
 * @param colorString - The color string (e.g., "0xffffff", "#ffffff", "ffffff")
 * @returns The standardized hexadecimal color string (e.g., "0xFFFFFF")
 */
export function toStandardHexColor(colorString: string): string {
    if (!colorString || typeof colorString !== 'string') {
        // throw new Error('Color string must be a non-empty string');
        alert('Color string must be a non-empty string');
        return ""
    }

    // Remove leading/trailing whitespace
    const trimmed = colorString.trim();

    // Handle different input formats
    let hexValue: string;
    let prefix: string = '0x';

    if (trimmed.startsWith('0x') || trimmed.startsWith('0X')) {
        // Already has 0x prefix
        hexValue = trimmed.substring(2);
        prefix = '0x';
    } else if (trimmed.startsWith('#')) {
        // Has # prefix, convert to 0x format
        hexValue = trimmed.substring(1);
        prefix = '0x';
    } else {
        // No prefix, assume it's just hex digits
        hexValue = trimmed;
        prefix = '0x';
    }

    // Validate hex value (only contains 0-9, a-f, A-F)
    if (!/^[0-9A-Fa-f]+$/.test(hexValue)) {
        throw new Error(`Invalid hexadecimal color value: ${colorString}`);
    }

    // Convert to uppercase and return with 0x prefix
    return `${prefix}${hexValue.toUpperCase()}`;
}